import React, { useState } from 'react'
import { Button, Dropdown, Input } from 'semantic-ui-react'
import styled from 'styled-components'
import { bulkUpdateContacts } from 'utility/services/crm'
import { ALL_CRM_SCOPES, invalidateCrm, useCrmLookups, useCrmToast } from 'hooks/crm'

const MAX_BULK = 500

const Bar = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 10px;
    padding: 10px 14px;
    margin-bottom: 10px;
    border-radius: 8px;
    background: rgba(59, 130, 246, 0.15);
    border: 1px solid rgba(59, 130, 246, 0.4);
`

/**
 * Bulk actions for the selected contacts: owner, type or one tag (max 500 per call).
 * @param {{ selectedIds: number[], onClear: () => void }} props
 */
export const ContactBulkBar = ({ selectedIds, onClear }) => {
    const { lookups } = useCrmLookups()
    const { successToast, showError } = useCrmToast()
    const [owner, setOwner] = useState('')
    const [type, setType] = useState('')
    const [tag, setTag] = useState('')
    const [busy, setBusy] = useState(false)

    if (!selectedIds.length) return null
    const tooMany = selectedIds.length > MAX_BULK

    const apply = async (patch, label) => {
        setBusy(true)
        try {
            const result = await bulkUpdateContacts({ ContactIds: selectedIds, ...patch })
            successToast(`${label}: ${result.Updated} contact${result.Updated === 1 ? '' : 's'} updated`)
            invalidateCrm(...ALL_CRM_SCOPES)
            setOwner('')
            setType('')
            setTag('')
            onClear()
        } catch (err) {
            showError(err)
        } finally {
            setBusy(false)
        }
    }

    return (
        <Bar role="region" aria-label="Bulk actions">
            <strong>{selectedIds.length} selected</strong>
            {tooMany ? <span role="alert">Select {MAX_BULK} or fewer contacts for a bulk action.</span> : null}
            <Dropdown
                selection
                compact
                placeholder="Set owner"
                options={(lookups?.Users || []).map(u => ({ key: u.UserId, value: u.UserId, text: u.FullName }))}
                value={owner}
                onChange={(_, d) => setOwner(d.value)}
                disabled={busy || tooMany}
            />
            <Button size="small" disabled={owner === '' || busy || tooMany} onClick={() => apply({ OwnerUserId: Number(owner) }, 'Owner set')}>
                Apply owner
            </Button>
            <Dropdown
                selection
                compact
                placeholder="Set type"
                options={(lookups?.AccountTypes || []).map(t => ({ key: t, value: t, text: t }))}
                value={type}
                onChange={(_, d) => setType(d.value)}
                disabled={busy || tooMany}
            />
            <Button size="small" disabled={type === '' || busy || tooMany} onClick={() => apply({ ContactType: type }, 'Type set')}>
                Apply type
            </Button>
            <Input
                size="small"
                placeholder="Add tag"
                value={tag}
                onChange={(_, d) => setTag(d.value)}
                disabled={busy || tooMany}
                aria-label="Tag to add"
            />
            <Button size="small" disabled={!tag.trim() || busy || tooMany} onClick={() => apply({ AddTag: tag.trim() }, 'Tag added')}>
                Add tag
            </Button>
            <Button size="small" basic onClick={onClear} disabled={busy}>
                Clear selection
            </Button>
        </Bar>
    )
}
